// Generates a branded shareable rank card as a PNG Blob using canvas (no backend/LLM cost).
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Per-game colour themes; falls back to violet default for custom games.
const THEMES = {
  "Cyber Heist": { a: "#22d3ee", b: "#06b6d4", bg1: "#03121a", bg2: "#052436" },
  "Bio-Hazard Lab": { a: "#e879f9", b: "#a855f7", bg1: "#120a20", bg2: "#2a1038" },
  "Temple Vault": { a: "#fbbf24", b: "#f97316", bg1: "#1a1204", bg2: "#2a1806" },
};
const DEFAULT_THEME = { a: "#a78bfa", b: "#8b5cf6", bg1: "#0a0e17", bg2: "#1a1030" };
const themeFor = (name) => THEMES[name] || DEFAULT_THEME;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function makeRankCardBlob(entry, gameName) {
  const t = themeFor(gameName);
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // background
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, t.bg1);
  g.addColorStop(0.55, t.bg2);
  g.addColorStop(1, t.bg1);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // themed glow blob
  const rg = ctx.createRadialGradient(W * 0.8, 180, 40, W * 0.8, 180, 520);
  rg.addColorStop(0, t.a + "73");
  rg.addColorStop(1, t.a + "00");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, W, H);

  // brand
  ctx.fillStyle = t.a;
  ctx.font = "700 34px 'Outfit', sans-serif";
  ctx.fillText("🚚  ESCAPE ROOM EXPRESS", 70, 100);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 26px 'JetBrains Mono', monospace";
  ctx.fillText(gameName ? gameName.toUpperCase() : "ESCAPE ROOM ON WHEELS", 70, 145);

  // photo
  const img = await loadImage(entry.photo_url);
  const photoY = 200, photoH = 520;
  ctx.save();
  roundRect(ctx, 70, photoY, W - 140, photoH, 32);
  ctx.clip();
  if (img) {
    const ratio = Math.max((W - 140) / img.width, photoH / img.height);
    const dw = img.width * ratio, dh = img.height * ratio;
    ctx.drawImage(img, 70 + ((W - 140) - dw) / 2, photoY + (photoH - dh) / 2, dw, dh);
  } else {
    ctx.fillStyle = "#131a29";
    ctx.fillRect(70, photoY, W - 140, photoH);
    ctx.fillStyle = "#334155";
    ctx.font = "800 120px 'Outfit', sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("🏆", W / 2, photoY + photoH / 2 + 40);
    ctx.textAlign = "left";
  }
  ctx.restore();
  // themed border on photo
  ctx.strokeStyle = t.a + "66";
  ctx.lineWidth = 4;
  roundRect(ctx, 70, photoY, W - 140, photoH, 32);
  ctx.stroke();

  // rank badge
  ctx.fillStyle = entry.rank === 1 ? "#f59e0b" : entry.rank === 2 ? "#cbd5e1" : entry.rank === 3 ? "#fb923c" : t.b;
  ctx.font = "900 130px 'Outfit', sans-serif";
  ctx.fillText(entry.rank ? `#${entry.rank}` : "🏁", 70, 890);

  // time
  ctx.textAlign = "right";
  ctx.fillStyle = t.a;
  ctx.font = "800 130px 'JetBrains Mono', monospace";
  ctx.fillText(fmt(entry.completion_seconds), W - 70, 890);
  ctx.textAlign = "left";
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 28px 'JetBrains Mono', monospace";
  ctx.textAlign = "right";
  ctx.fillText("ESCAPE TIME", W - 70, 930);
  ctx.textAlign = "left";

  // team + org
  ctx.fillStyle = "#f8fafc";
  ctx.font = "800 68px 'Outfit', sans-serif";
  ctx.fillText(entry.team_name.slice(0, 20), 70, 1010);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 38px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText(entry.org_name || "", 70, 1060);

  // location badge
  ctx.fillStyle = t.a + "26";
  const locText = `📍 ${entry.location_name || ""}`;
  ctx.font = "600 34px 'Plus Jakarta Sans', sans-serif";
  const lw = ctx.measureText(locText).width + 60;
  roundRect(ctx, 70, 1100, lw, 66, 33);
  ctx.fill();
  ctx.fillStyle = "#f1f5f9";
  ctx.fillText(locText, 100, 1143);

  // footer CTA
  ctx.fillStyle = t.b;
  roundRect(ctx, 70, 1220, W - 140, 90, 24);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 40px 'Outfit', sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("Think you can beat us? Book now →", W / 2, 1278);
  ctx.textAlign = "left";

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

export { fmt };
